import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
} from '@mui/material';
import {
    WhatsApp as WhatsAppIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    Share as ShareIcon
} from '@mui/icons-material';
import BottomModal from './BottomModel';

interface ShareModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    shareUrl: string;
    shareText?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({ 
    open, 
    onClose, 
    title, 
    shareUrl, 
    shareText 
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    const handleWhatsAppShare = () => {
        const message = `Check out this: ${shareUrl}`;
        const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(url, '_blank');
    };

    const handleFacebookShare = () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        window.open(url, '_blank');
    };

    const handleInstagramShare = () => {
        // Instagram doesn't support direct URL sharing, so we'll copy the link
        handleCopyLink();
    };

    const shareOptions = [
        {
            name: 'Instagram',
            icon: <InstagramIcon sx={{ fontSize: 28, color: '#E4405F' }} />,
            color: '#E4405F',
            onClick: handleInstagramShare
        },
        {
            name: 'Facebook',
            icon: <FacebookIcon sx={{ fontSize: 28, color: '#1877F2' }} />,
            color: '#1877F2',
            onClick: handleFacebookShare
        },
        {
            name: 'WhatsApp',
            icon: <WhatsAppIcon sx={{ fontSize: 28, color: '#25D366' }} />,
            color: '#25D366',
            onClick: handleWhatsAppShare
        },
        {
            name: 'More',
            icon: <ShareIcon sx={{ fontSize: 28, color: '#6B7280' }} />,
            color: '#6B7280',
            onClick: () => {
                if (navigator.share) {
                    navigator.share({
                        title: title,
                        text: shareText || `Check out this: ${title}`,
                        url: shareUrl
                    });
                } else {
                    handleCopyLink();
                }
            }
        }
    ];

    return (
        <BottomModal
            open={open}
            onClose={onClose}
            title="Share"
        >
            <Box sx={{ p: 3 }}>
                {/* Tap to Copy Section */}
                <Box
                    sx={{
                        backgroundColor: '#F8F9FA',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        p: 2,
                        mb: 3,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#F1F3F4',
                        }
                    }}
                    onClick={handleCopyLink}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#6B7280',
                            textAlign: 'center',
                            fontWeight: 500
                        }}
                    >
                        Tap to copy link
                    </Typography>
                </Box>
                
                <Grid container spacing={3} sx={{ mb: 3, justifyContent: 'center' }}>
                    {shareOptions.map((option, index) => (
                        <Grid sx={{xs:3}} key={index}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                        transition: 'transform 0.2s ease-in-out'
                                    }
                                }}
                                onClick={option.onClick}
                            >
                                <Paper
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#F8F9FA',
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '12px',
                                        mb: 1,
                                        boxShadow: 'none'
                                    }}
                                >
                                    {option.icon}
                                </Paper>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#374151',
                                        textAlign: 'center'
                                    }}
                                >
                                    {option.name}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                
                {copied && (
                    <Box
                        sx={{
                            backgroundColor: '#D1FAE5',
                            border: '1px solid #A7F3D0',
                            borderRadius: '8px',
                            p: 2,
                            textAlign: 'center'
                        }}
                    >
                        <Typography color="success.main" sx={{ fontSize: '14px', fontWeight: 500 }}>
                            Link copied to clipboard!
                        </Typography>
                    </Box>
                )}
            </Box>
        </BottomModal>
    );
};

export default ShareModal;